import './styling/base.css';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import Navbar from './components/Navbar';
import asignIconwhite from './assets/logos/asign-icon-white.svg';
import playIcon from './assets/icons/play-solid.svg';
import caretLeftIcon from './assets/icons/caret-left-solid.svg';
import caretRightIcon from './assets/icons/caret-right-solid.svg';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Overview from './pages/Overview';
import Forms from './pages/Forms';
import Form1 from './pages/Form1';
import Form2 from './pages/Form2';
import { useTranslation } from 'react-i18next';

function LangWrapper({ lang, children }) {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [lang, i18n]);
  return <>{children}</>;
}

const EN_TO_FR = {
  '/': '/fr',
  '/forms': '/fr/formulaires',
  '/disability-tax-credit': '/fr/credit-impot-pour-personnes-handicapees',
  '/canada-disability-benefit': '/fr/prestation-canadienne-pour-les-personnes-handicapees',
};
const FR_TO_EN = {
  '/fr': '/',
  '/fr/formulaires': '/forms',
  '/fr/credit-impot-pour-personnes-handicapees': '/disability-tax-credit',
  '/fr/prestation-canadienne-pour-les-personnes-handicapees': '/canada-disability-benefit',
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [videoSidebarOpen, setVideoSidebarOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [videoLabels, setVideoLabels] = useState([]);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [allVideoUrls, setAllVideoUrls] = useState([]);
  const [registeredSections, setRegisteredSections] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const sidebarRef = useRef(null);
  const swipeRef = useRef({ startY: 0, currentY: 0, active: false });
  const tabClickSuppressRef = useRef(false);
  const { i18n, t } = useTranslation();

  // When language changes while sidebar is open, immediately switch to the correct tab
  useEffect(() => {
    if (!videoSidebarOpen || allVideoUrls.length === 0) return;
    const defaultIdx = i18n.language === 'fr' ? 1 : 0;
    setSelectedVideoIndex(defaultIdx);
    setCurrentVideoUrl(allVideoUrls[defaultIdx]);
    setIsPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Lock body scroll on mobile while a sidebar is open. Pinning body to
  // position: fixed at -scrollY prevents the page underneath from scrolling
  // and lets us restore the scroll position on close (overflow: hidden on
  // html/body resets it to the top on iOS, which is why we don't use that).
  //
  // useLayoutEffect (not useEffect) + a forced synchronous reflow (void
  // body.offsetHeight) before the scrollTo: this is the combination that
  // stops the brief "snap to top, then scroll back" flash on iOS Safari.
  // The reflow forces the document height to be recomputed against the
  // newly-unfixed body before scrollTo runs, so the scroll lands in the
  // same pre-paint frame as the unlock.
  useLayoutEffect(() => {
    const anyOpen = sidebarOpen || videoSidebarOpen;
    if (!anyOpen || window.innerWidth > 1089) return undefined;

    const scrollY = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      // Force a synchronous layout flush so iOS knows the document's real
      // scroll height before we ask it to scroll. Without this, iOS can
      // composite the unfixed-body frame at scrollTop=0 before applying
      // our scrollTo on the next frame.
      // eslint-disable-next-line no-unused-expressions
      body.offsetHeight;
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    };
  }, [sidebarOpen, videoSidebarOpen]);

  const registerSection = useCallback((data) => {
    setRegisteredSections(prev => {
      if (prev.find(s => s.sectionId === data.sectionId)) return prev;
      return [...prev, data];
    });
  }, []);

  const unregisterSection = useCallback((sectionId) => {
    setRegisteredSections(prev => prev.filter(s => s.sectionId !== sectionId));
  }, []);

  const handleVideoSidebarToggle = () => {
    if (videoSidebarOpen) {
      setActiveSectionId(null);
      setVideoSidebarOpen(false);
      setIsPlaying(false);
    } else {
      if (!activeSectionId && registeredSections.length > 0) {
        const defaultSection = registeredSections.find(s => s.isDefault) || registeredSections[0];
        const defaultIdx = i18n.language === 'fr' ? 1 : 0;
        setActiveSectionId(defaultSection.sectionId);
        setCurrentVideoUrl(defaultSection.videos[defaultIdx]);
        setAllVideoUrls(defaultSection.videos);
        setVideoLabels(defaultSection.labels);
        setSelectedVideoIndex(defaultIdx);
      }
      setVideoSidebarOpen(true);
    }
  };

  const togglePlayPause = () => {
    if (currentVideoUrl && currentVideoUrl.includes('vimeo.com')) {
      if (iframeRef.current) {
        const method = isPlaying ? 'pause' : 'play';
        iframeRef.current.contentWindow.postMessage(JSON.stringify({ method }), '*');
      }
    } else {
      if (videoRef.current) {
        if (isPlaying) videoRef.current.pause();
        else videoRef.current.play();
      }
    }
    setIsPlaying(!isPlaying);
  };

  const navigateSection = (direction) => {
    if (registeredSections.length === 0) return;
    const currentIndex = registeredSections.findIndex(s => s.sectionId === activeSectionId);
    let newIndex;
    if (currentIndex === -1) {
      newIndex = 0;
    } else {
      newIndex = currentIndex + direction;
      if (newIndex < 0) newIndex = registeredSections.length - 1;
      if (newIndex >= registeredSections.length) newIndex = 0;
    }
    const section = registeredSections[newIndex];
    const defaultIdx = i18n.language === 'fr' ? 1 : 0;
    setActiveSectionId(section.sectionId);
    setCurrentVideoUrl(section.videos[defaultIdx]);
    setAllVideoUrls(section.videos);
    setVideoLabels(section.labels);
    setSelectedVideoIndex(defaultIdx);
    setIsPlaying(false);
    const el = section.getEl && section.getEl();
    // CSS scroll-margin-top on .tab-section-wrapper / .form-tab-overlay
    // provides the breathing room above the section.
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ----- Mobile swipe handlers on the sidebar peek/handle area -----
  // Swipe up on the bar = open. Swipe down = close. Only active on mobile
  // (the desktop sidebar slides horizontally, not vertically).
  const SWIPE_THRESHOLD_PX = 40;
  const HANDLE_AREA_PX = 110; // ~87px peek bar + tab protrusion

  const handleSidebarTouchStart = (e) => {
    tabClickSuppressRef.current = false;
    if (window.innerWidth > 1089 || !sidebarRef.current) return;
    const touch = e.touches[0];
    const rect = sidebarRef.current.getBoundingClientRect();
    const distanceFromTop = touch.clientY - rect.top;
    // Only register swipes that start in the top handle/peek area so the
    // content area below can still scroll normally when the sidebar is open.
    if (distanceFromTop > HANDLE_AREA_PX) {
      swipeRef.current.active = false;
      return;
    }
    swipeRef.current.startY = touch.clientY;
    swipeRef.current.currentY = touch.clientY;
    swipeRef.current.active = true;
  };

  const handleSidebarTouchMove = (e) => {
    if (!swipeRef.current.active) return;
    swipeRef.current.currentY = e.touches[0].clientY;
  };

  const handleSidebarTouchEnd = () => {
    if (!swipeRef.current.active) return;
    const delta = swipeRef.current.currentY - swipeRef.current.startY;
    swipeRef.current.active = false;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    // Suppress the click that fires after touchend if the swipe started on the tab
    tabClickSuppressRef.current = true;
    if (delta < 0 && !videoSidebarOpen) {
      handleVideoSidebarToggle();
    } else if (delta > 0 && videoSidebarOpen) {
      handleVideoSidebarToggle();
    }
  };

  const handleTabClick = () => {
    if (tabClickSuppressRef.current) {
      tabClickSuppressRef.current = false;
      return;
    }
    handleVideoSidebarToggle();
  };

  return (
    <>
      <ScrollToTop />
      <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} videoSidebarOpen={videoSidebarOpen} onVideoSidebarToggle={handleVideoSidebarToggle} />
      <main className={`main-content ${sidebarOpen ? 'sidebar-open' : ''} ${videoSidebarOpen ? 'video-sidebar-open' : ''}`}>
        <Routes>
          {/* English routes */}
          <Route path="/" element={<LangWrapper lang="en"><Overview activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/forms" element={<LangWrapper lang="en"><Forms activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/disability-tax-credit" element={<LangWrapper lang="en"><Form1 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/canada-disability-benefit" element={<LangWrapper lang="en"><Form2 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          {/* French routes */}
          <Route path="/fr" element={<LangWrapper lang="fr"><Overview activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/fr/formulaires" element={<LangWrapper lang="fr"><Forms activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/fr/credit-impot-pour-personnes-handicapees" element={<LangWrapper lang="fr"><Form1 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
          <Route path="/fr/prestation-canadienne-pour-les-personnes-handicapees" element={<LangWrapper lang="fr"><Form2 activeSectionId={activeSectionId} setActiveSectionId={setActiveSectionId} setCurrentVideoUrl={setCurrentVideoUrl} setVideoSidebarOpen={setVideoSidebarOpen} setVideoLabels={setVideoLabels} setSelectedVideoIndex={setSelectedVideoIndex} setAllVideoUrls={setAllVideoUrls} registerSection={registerSection} unregisterSection={unregisterSection} /></LangWrapper>} />
        </Routes>
      </main>
      
      {/* BACKDROP — mobile only, blocks page scroll/touch behind the sidebar */}
      {videoSidebarOpen && (
        <div className="video-sidebar-backdrop" onClick={handleVideoSidebarToggle} />
      )}

      {/* GLOBAL VIDEO SIDEBAR */}
      <aside
        ref={sidebarRef}
        className={`video-sidebar-global ${videoSidebarOpen ? 'open' : ''}`}
        onTouchStart={handleSidebarTouchStart}
        onTouchMove={handleSidebarTouchMove}
        onTouchEnd={handleSidebarTouchEnd}
      >
        {/* MOBILE HANDLE TAB — lives inside the sidebar so it always moves with it,
            keeping the tab perfectly aligned with the peek bar on every screen height */}
        <div className={`video-sidebar-tab ${videoSidebarOpen ? 'open' : ''}`} onClick={handleTabClick} title="Toggle video sidebar">
          <img src={asignIconwhite} alt="ASL Icon" className="video-sidebar-tab-icon" />
        </div>
        <div className={`video-sidebar-content ${videoSidebarOpen ? 'open' : ''}`}>
          {/* Language Toggle Tabs */}
          {videoLabels.length > 0 && (
            <div className="video-language-tabs">
              {videoLabels.map((label, index) => (
                <button
                  key={index}
                  className={`video-tab ${selectedVideoIndex === index ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedVideoIndex(index);
                    setCurrentVideoUrl(allVideoUrls[index]);
                    setIsPlaying(false);
                    const targetLang = index === 0 ? 'en' : 'fr';
                    if (i18n.language !== targetLang) {
                      const path = targetLang === 'fr'
                        ? (EN_TO_FR[location.pathname] || '/fr')
                        : (FR_TO_EN[location.pathname] || '/');
                      i18n.changeLanguage(targetLang);
                      navigate(path, { state: { preserveScroll: true } });
                    }
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="video-container"> 
            <div className="video-wrapper">
              {currentVideoUrl && currentVideoUrl.includes('vimeo.com') ? (
                <iframe
                  ref={iframeRef}
                  src={`https://player.vimeo.com/video/${currentVideoUrl.split('/').pop()}`}
                  className="video-player"
                  allowFullScreen
                  title="Vimeo Video"
                />
              ) : (
                <video
                  ref={videoRef}
                  controls
                  playsInline
                  webkit-playsinline="true"
                  className="video-player"
                  src={currentVideoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            <div className="video-title">
              <p className="">{activeSectionId ? t(activeSectionId) : ''}</p>
            </div>

            <div className="video-player-controls">
              <div className="video-play-btn" onClick={togglePlayPause}>
                {isPlaying ? '⏸' : <img src={playIcon} alt="Play" className="video-control-icon" />}
              </div>
              <div className="video-next">
                <button onClick={() => navigateSection(-1)} className="video-nav-btn">
                  <img src={caretLeftIcon} alt="Previous" className="video-control-icon" />
                </button>
                <button onClick={() => navigateSection(1)} className="video-nav-btn">
                  <img src={caretRightIcon} alt="Next" className="video-control-icon" />
                </button>
              </div>
            </div>

            {/* Per-section transcript. Text is authored per section under the
                `transcripts` namespace and follows the active video's language
                (ASL ↔ EN, LSQ ↔ FR). Falls back to a placeholder until content
                is supplied. */}
            {activeSectionId && (() => {
              const transcriptKey = activeSectionId.replace(/^sections\./, 'transcripts.');
              const transcriptText = t(transcriptKey, { defaultValue: '' });
              return (
                <details className="video-transcript">
                  <summary className="video-transcript-summary">
                    <span>{t('transcript.label')}</span>
                    <span className="video-transcript-toggle" aria-hidden="true"></span>
                  </summary>
                  <div className="video-transcript-body">
                    <p dangerouslySetInnerHTML={{ __html: transcriptText || t('transcript.placeholder') }} />
                  </div>
                </details>
              );
            })()}

          </div>

            <p className={`asl-footer ${videoSidebarOpen ? 'open' : ''}`}>Translations by <u><b><a href="https://asign.ca/" target="_blank" rel="noopener noreferrer">Asign</a></b></u></p>

        </div>
      </aside>
      
      <Footer
        activeSectionId={activeSectionId}
        setActiveSectionId={setActiveSectionId}
        setCurrentVideoUrl={setCurrentVideoUrl}
        setVideoSidebarOpen={setVideoSidebarOpen}
        setVideoLabels={setVideoLabels}
        setSelectedVideoIndex={setSelectedVideoIndex}
        setAllVideoUrls={setAllVideoUrls}
        registerSection={registerSection}
        unregisterSection={unregisterSection}
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
